<template>
    <article :class="[ 'task-item', { 'disabled': disabled } ]">
        <div class="task-content">
            <header class="header">
                <div class="app-title">
                    <app-router-link :appId="appId" :appName="appName" :url="url" class="app-url">{{ appName }}</app-router-link>
                </div>
                <p class="task-date">{{ createdAt | humanize }}</p>
            </header>
            <section class="task-body">
                <p class="task-title" v-if="hasTitle">{{ title }}</p>
                <p class="task-description">{{ truncatedDescription }}</p>
            </section>
            <footer class="footer">
                <button class="mark" @click="markAsRead()" v-if="!disabled"><i class="fas fa-envelope-open-text"></i>Read</button>
                <button class="mark" @click="markAsUnread()" v-else><i class="fas fa-envelope"></i>Unread</button>
            </footer>
        </div>
    </article>
</template>

<script>
import AppRouterLink from "@/components/AppRouterLink";
import TasksService from "@/services/TasksService";

export default {
    name: "task-item",
    components: {
        AppRouterLink
    },
    props: {
        taskId: {
            required: true,
            type: Number
        },
        title: {
            required: false,
            default: null
        },
        description: {
            required: false,
            default: null
        },
        image: {
            required: false,
            type: String,
            default: ""
        },
        due: {
            required: false,
        },
        createdAt: {
            required: true,
        },
        disabled: {
            required: true,
            type: Boolean
        },
        url: {
            required: true,
            type: String
        },
        appId: {
            required: true,
            type: Number
        },
        appName: {
            required: true,
            type: String
        }
    },
    methods: {
        async markAsRead() {
            try {
                await TasksService.markTaskAsRead(this.taskId, true);
                this.$emit("taskMarked", true);
            } catch(e) {
                //
            }
        },
        async markAsUnread() {
            try {
                await TasksService.markTaskAsRead(this.taskId, false);
                this.$emit("taskMarked", false);
            } catch(e) {
                //
            }
        },
    },
    computed: {
        hasTitle() {
            return !!(this.title && this.title.length);
        },
        truncatedDescription() {
            return this.description;
        }
    }
}
</script>

<style lang="scss" scoped>
@mixin disabledTask() {
    color: $gray;
    font-style: italic;
}
@mixin disabledTaskDark() {
    color: $dark-gray;
    font-style: italic;
}
.task-item {
    padding: 15px 20px;
    border-bottom: 1px solid $light-gray;
    @include fastestTransition;
    .header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin: 0 0 10px 0;
        .app-title {
            display: flex;
            align-items: center;
            .app-avatar {
                width: 22px;
                height: 22px;
                border-radius: 50%;
                margin: 0 5px 0 0;
            }
            .app-url {
                padding: 0;
                font-family: $roboto;
                font-weight: 700;
                font-size: 14px;
                color: $primary;
                background: none;
                @include removeBootstrapOutline;
                cursor: pointer;
                &:hover {
                    text-decoration: underline;
                }
            }
        }
        .task-date {
            display: flex;
            align-items: center;
            margin: 0;
            font-family: $roboto;
            font-size: 12px;
            color: $dark-gray;
        }
    }
    .task-body {
        margin: 0 0 10px 0;
        .task-title {
            width: 45%;
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;
            font-family: $lato;
            font-weight: 500;
            font-size: 13px;
            color: $darker-gray;
            margin: 0 0 2px 0;
        }
        .task-description {
            font-family: $lato;
            font-weight: normal;
            font-size: 13px;
            color: $dark-gray;
            line-height: 1.3;
            text-overflow: ellipsis;
            white-space: nowrap;
            overflow: hidden;
        }
    }
    .footer {
        .mark {
            padding: 0;
            font-family: $raleway;
            font-size: 12px;
            font-weight: 700;
            text-transform: uppercase;
            color: $dark-gray;
            background: none;
            border-radius: 3px;
            @include removeBootstrapOutline;
            cursor: pointer;
            i {
                margin: 0 7px 0 0;
            }
            &:hover {
                color: $darker-gray;
            }
        }
    }
    &:hover {
        background-color: $light-gray;
    }
    &.disabled {
        .header {
            .app-url, .task-date {
                @include disabledTask;
            }
        }
        .task-body {
            .task-title, .task-description {
                @include disabledTask;
            }
        }
        .footer {
            .mark {
                @include disabledTask;
                font-style: normal;
                &:hover {
                   color: $darker-gray;
                }
            }
        }
    }
    &:last-child {
        border-bottom: 0;
    }
}

.dark {
    .task-item {
        border-bottom: 1px solid $extra-dark-gray;
        .task-body {
            .task-title {
                color: $lighter-gray;
            }
            .task-description {
                color: $dark-gray;
            }
        }
        .footer {
            .mark {
                color: $dark-gray;
                &:hover {
                    color: $gray;
                }
            }
        }
        &:hover {
            background-color: $extra-dark-gray;
        }
        &.disabled {
            .header {
                .app-url, .task-date {
                    @include disabledTaskDark;
                }
            }
            .task-body {
                .task-title, .task-description {
                    @include disabledTaskDark;
                }
            }
            .footer {
                .mark {
                    @include disabledTaskDark;
                    font-style: normal;
                    &:hover {
                        color: $darker-gray;
                    }
                }
            }
        }
        &:last-child {
            border-bottom: 0;
        }
    }
}
</style>
